import { endOfDay, isWithinInterval, parse, startOfDay } from 'date-fns';

import { SUBMITTED_AT_DATE_FILTER_FORMAT, SUBMITTED_AT_RAW_ROW_KEY } from './form-submission-constants.util';

const SUBMITTED_AT_DISPLAY_MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * Converts API `created_at` to epoch ms. Supports ISO strings and Unix epoch (seconds or ms).
 * @param {unknown} createdAt
 * @returns {number | null}
 */
const numberCreatedAtToMs = (createdAt) => {
  if (!Number.isFinite(createdAt)) {
    return null;
  }
  return createdAt < 1_000_000_000_000 ? Math.round(createdAt * 1000) : Math.round(createdAt);
};

const coerceCreatedAtToMs = (createdAt) => {
  const parsed = new Date(createdAt);
  return Number.isNaN(parsed.getTime()) ? null : parsed.getTime();
};

export const submissionCreatedAtToMs = (createdAt) => {
  if (createdAt == null || createdAt === '' || typeof createdAt === 'boolean') {
    return null;
  }
  if (typeof createdAt === 'number') {
    return numberCreatedAtToMs(createdAt);
  }
  return coerceCreatedAtToMs(createdAt);
};

/**
 * Renders submission `created_at` in the DataGrid using the viewer's local timezone
 * (`Date` getters / host default).
 * @param {Date} d
 * @returns {string}
 */
const formatDisplayDateTime = (d) => {
  const day = d.getDate();
  const month = SUBMITTED_AT_DISPLAY_MONTHS[d.getMonth()];
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} - ${hours}:${minutes}`;
};

export const formatFormSubmissionCreatedAtForDisplay = (createdAt) => {
  const ms = submissionCreatedAtToMs(createdAt);
  if (ms === null) {
    return '';
  }
  return formatDisplayDateTime(new Date(ms));
};

const isCoercibleDateRaw = (raw) =>
  raw != null && raw !== '' && (typeof raw === 'string' || typeof raw === 'number');

const coerceToDate = (raw) => {
  if (!isCoercibleDateRaw(raw)) {
    return null;
  }
  const rowDate = new Date(String(raw));
  return Number.isNaN(rowDate.getTime()) ? null : rowDate;
};

const parseSubmittedRowDate = (raw) => coerceToDate(raw);

const splitFilterDateParts = (filterValue) =>
  String(filterValue)
    .trim()
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const parseFilterDateRange = (parts) => {
  const start = parse(parts[0], SUBMITTED_AT_DATE_FILTER_FORMAT, new Date());
  const end =
    parts.length > 1
      ? parse(parts[1], SUBMITTED_AT_DATE_FILTER_FORMAT, new Date())
      : start;
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }
  return { start: startOfDay(start), end: endOfDay(end) };
};

/**
 * Client-side date range filter for Submitted column (DataGrid `paginationMode="client"`).
 * Filter value shape from DateRangeFilter: `start,end` formatted with {@link SUBMITTED_AT_DATE_FILTER_FORMAT}.
 * @param {string} _columnName
 * @param {string} filterValue
 * @param {{ rowData?: Record<string, unknown> }} data
 * @returns {typeof data | null}
 */
export const submittedAtDateRangeOnCustomFilter = (_columnName, filterValue, data) => {
  const rowDate = parseSubmittedRowDate(data.rowData?.[SUBMITTED_AT_RAW_ROW_KEY]);
  if (!rowDate) {
    return null;
  }
  const parts = splitFilterDateParts(filterValue);
  if (parts.length === 0) {
    return data;
  }
  const interval = parseFilterDateRange(parts);
  if (!interval) {
    return data;
  }
  return isWithinInterval(rowDate, interval) ? data : null;
};
